import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Users, Calendar, Clock, User, Search, AlertTriangle } from 'lucide-react';
import { testApi, classApi, studentApi } from '@/lib/api';
import { Test, Class, Student } from '@/types';
import { useToast } from '@/components/ui/use-toast';

const TestAssignEditPage = () => {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [test, setTest] = useState<Test | null>(null);
  const [classes, setClasses] = useState<Class[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [modificationValidation, setModificationValidation] = useState<any>(null);

  useEffect(() => {
    if (testId) {
      loadData();
      loadModificationValidation();
    }
  }, [testId]);

  const loadData = async () => {
    try {
      setDataLoading(true);
      const [testResponse, classesData, studentsData] = await Promise.all([
        testApi.getActualTestById(testId!),
        classApi.getAll(),
        studentApi.getAll()
      ]);

      setTest(testResponse);
      setClasses(Array.isArray(classesData) ? classesData : (classesData as any)?.content || []);
      setStudents(Array.isArray(studentsData) ? studentsData : (studentsData as any)?.content || []);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast({
        title: "Error",
        description: "Failed to load data",
        variant: "destructive",
      });
      navigate('/tests');
    } finally {
      setDataLoading(false);
    }
  };

  const loadModificationValidation = async () => {
    try {
      const validation = await testApi.validateTestModification(testId!);
      setModificationValidation(validation);
    } catch (error) {
      console.error('Failed to load modification validation:', error);
      // Don't show error toast for validation failure, just log it
    }
  };

  const handleClassSelection = (classId: string, checked: boolean) => {
    if (checked) {
      setSelectedClassIds(prev => [...prev, classId]);
    } else {
      setSelectedClassIds(prev => prev.filter(id => id !== classId));
    }
  };

  const handleStudentSelection = (studentId: string, checked: boolean) => {
    if (checked) {
      setSelectedStudentIds(prev => [...prev, studentId]);
    } else {
      setSelectedStudentIds(prev => prev.filter(id => id !== studentId));
    }
  };

  const handleAssignSubmit = async () => {
    if (!test) return;

    // Check if students have already taken the test
    if (modificationValidation && !modificationValidation.canModify) {
      toast({
        title: "Cannot Modify Test",
        description: modificationValidation.reason || "Students have already taken this test. Please create a new test instead.",
        variant: "destructive",
      });
      return;
    }

    if (selectedClassIds.length === 0 && selectedStudentIds.length === 0) {
      toast({
        title: "Error",
        description: "Please select at least one class or student to assign the test.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const assignmentData = {
        classIds: selectedClassIds.length > 0 ? selectedClassIds : undefined,
        studentIds: selectedStudentIds.length > 0 ? selectedStudentIds : undefined,
      };

      await testApi.assign(test.id, assignmentData);
      toast({
        title: "Success",
        description: "Test assigned successfully",
      });
      navigate('/tests');
    } catch (error) {
      console.error('Failed to assign test:', error);
      toast({
        title: "Error",
        description: "Failed to assign test",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/tests');
  };

  const filteredStudents = students.filter(student =>
    (student.firstName + ' ' + student.lastName).toLowerCase().includes(searchTerm.toLowerCase()) ||
    student.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTotalSelectedStudents = () => {
    const classStudentCount = selectedClassIds.reduce((total, classId) => {
      const classData = classes.find(c => c.id === classId);
      return total + ((classData as any)?.totalStudents || (classData as any)?.studentCount || 0);
    }, 0);
    return classStudentCount + selectedStudentIds.length;
  };

  if (dataLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading assignment options...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-muted-foreground">Test not found</p>
          <Button onClick={handleBack} className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Tests
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" onClick={handleBack}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Tests
        </Button>
        <div>
          <h1 className="text-2xl font-bold">Assign Test</h1>
          <p className="text-muted-foreground">Assign test to classes or specific students</p>
        </div>
      </div>

      {/* Test Information */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Test Details
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Title</p>
              <p className="font-medium">{test.title}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Subject</p>
              <p className="font-medium">{test.subjects?.[0]?.subjectName || test.subjectName || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Questions</p>
              <p className="font-medium">{test.totalQuestions}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Duration</p>
              <p className="font-medium flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {test.duration} minutes
              </p>
            </div>
          </div>
          {test.description && (
            <div className="mt-4">
              <p className="text-sm font-medium text-muted-foreground">Description</p>
              <p className="text-sm">{test.description}</p>
            </div>
          )}

          {/* Schedule Information */}
          {(test.status === 'SCHEDULED' || test.status === 'ON_DEMAND') && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-blue-600" />
                <span className="font-medium text-blue-900">Schedule Information</span>
              </div>
              {test.status === 'SCHEDULED' && (
                <div className="text-sm text-blue-800">
                  <p>Start: {test.startTime ? new Date(test.startTime).toLocaleString() : 'Not set'}</p>
                  <p>End: {test.endTime ? new Date(test.endTime).toLocaleString() : 'Not set'}</p>
                </div>
              )}
              {test.status === 'ON_DEMAND' && (
                <div className="text-sm text-blue-800">
                  <p>Available from: Now</p>
                  <p>Available until: {test.onDemandTestStartDate ? new Date(test.onDemandTestStartDate).toLocaleString() : 'Not set'}</p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modification Warnings */}
      {modificationValidation && (
        <div className="mb-6">
          {!modificationValidation.canModify && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Cannot modify this test:</strong> {modificationValidation.reason}
                <br />
                <span className="text-sm">
                  Students have already taken this test ({modificationValidation.studentParticipationCount} participants).
                  Please create a new test instead.
                </span>
              </AlertDescription>
            </Alert>
          )}

          {modificationValidation.canModify && modificationValidation.isInProgress && (
            <Alert variant="destructive" className="mb-4">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <strong>Warning:</strong> This test is currently in progress.
                <br />
                <span className="text-sm">
                  Modifying test assignments while students are taking it may disrupt the test and confuse students.
                  Proceed with caution.
                </span>
              </AlertDescription>
            </Alert>
          )}

          {modificationValidation.canModify && modificationValidation.studentParticipationCount > 0 && !modificationValidation.isInProgress && (
            <Alert className="mb-4">
              <Users className="h-4 w-4" />
              <AlertDescription>
                <strong>Note:</strong> {modificationValidation.studentParticipationCount} students have already taken this test.
                <br />
                <span className="text-sm">
                  You can still modify assignments, but consider the impact on students who have already completed the test.
                </span>
              </AlertDescription>
            </Alert>
          )}
        </div>
      )}

      {/* Assignment Form */}
      <Card>
        <CardHeader>
          <CardTitle>Assignment Options</CardTitle>
          <p className="text-sm text-muted-foreground">
            Select classes and/or individual students to assign this test to.
          </p>
        </CardHeader>
        <CardContent>
          {modificationValidation && !modificationValidation.canModify ? (
            <div className="text-center py-8">
              <AlertTriangle className="h-12 w-12 text-destructive mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Test Cannot Be Modified</h3>
              <p className="text-muted-foreground mb-4">
                This test cannot be modified because students have already taken it.
              </p>
              <Button onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Tests
              </Button>
            </div>
          ) : (
            <>
            <Tabs defaultValue="classes" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="classes" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Classes ({selectedClassIds.length})
              </TabsTrigger>
              <TabsTrigger value="students" className="flex items-center gap-2">
                <User className="h-4 w-4" />
                Individual Students ({selectedStudentIds.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="classes" className="space-y-4">
              <div className="text-sm text-muted-foreground">
                Select entire classes to assign the test to all students in those classes.
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                {classes.map((classItem) => (
                  <div
                    key={classItem.id}
                    className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50"
                  >
                    <Checkbox
                      id={`class-${classItem.id}`}
                      checked={selectedClassIds.includes(classItem.id)}
                      onCheckedChange={(checked) =>
                        handleClassSelection(classItem.id, checked === true)
                      }
                    />
                    <Label
                      htmlFor={`class-${classItem.id}`}
                      className="flex-1 cursor-pointer"
                    >
                      <div>
                        <div className="font-medium">{classItem.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {(classItem as any)?.totalStudents || (classItem as any)?.studentCount || 0} students
                        </div>
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="students" className="space-y-4">
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  Select individual students to assign the test to.
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search students by name or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto">
                {filteredStudents.map((student) => (
                  <div
                    key={student.id}
                    className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-muted/50"
                  >
                    <Checkbox
                      id={`student-${student.id}`}
                      checked={selectedStudentIds.includes(student.id)}
                      onCheckedChange={(checked) =>
                        handleStudentSelection(student.id, checked === true)
                      }
                    />
                    <Label
                      htmlFor={`student-${student.id}`}
                      className="flex-1 cursor-pointer"
                    >
                      <div>
                        <div className="font-medium">{student.firstName} {student.lastName}</div>
                        <div className="text-sm text-muted-foreground">
                          {student.email}
                        </div>
                      </div>
                    </Label>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>

            {/* Selection Summary */}
            {(selectedClassIds.length > 0 || selectedStudentIds.length > 0) && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="text-sm font-medium text-blue-900">
                  Assignment Summary
                </div>
                <div className="text-sm text-blue-700 mt-1">
                  Total students to be assigned: {getTotalSelectedStudents()}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between mt-6">
              <Button variant="outline" onClick={handleBack}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Tests
              </Button>

              <Button
                onClick={handleAssignSubmit}
                disabled={loading || (selectedClassIds.length === 0 && selectedStudentIds.length === 0)}
              >
                <Users className="mr-2 h-4 w-4" />
                Assign Test
              </Button>
            </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TestAssignEditPage;
